import * as React from 'react';
import * as echarts from 'echarts/core';
import { FunnelChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';

import type {
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
    FunnelSeriesOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | FunnelSeriesOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    FunnelChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface Props {
    totals: {
        visits: number;
        wishlist: number;
        registered: number;
        inquiries: number;
        orders: number;
    };
}

const FunnelSummaryChart: React.FC<Props> = ({ totals }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        const data = [
            { name: 'Visits', value: totals.visits || 0 },
            { name: 'Wishlist', value: totals.wishlist || 0 },
            { name: 'Registered', value: totals.registered || 0 },
            { name: 'Inquiry', value: totals.inquiries || 0 },
            { name: 'Orders', value: totals.orders || 0 },
        ];

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const option: ECOption = {
                tooltip: {
                    trigger: 'item',
                    formatter: (p: any) => `${p.name}: ${p.value}`,
                },
                legend: {
                    orient: 'vertical',
                    left: 'left',
                    data: data.map(d => d.name),
                },
                series: [
                    {
                        name: 'Funnel',
                        type: 'funnel',
                        left: '25%',
                        top: 20,
                        bottom: 20,
                        width: '60%',
                        sort: 'descending',
                        gap: 4,
                        label: {
                            show: true,
                            formatter: '{b}: {c}',
                        },
                        data,
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [totals]);

    return <div ref={chartRef} style={{ width: '100%', height: 320 }} />;
};

export default FunnelSummaryChart;

